import { Box, Button, styled } from '@mui/material'
import { refreshComputerList, useTerminateSession } from 'hooks'
import moment from 'moment'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ComputerSessionHistoryDetails from '../SessionHistoryDetail'
import { Avatar, Tooltip } from 'packages/eid-ui'
import { getFullImageUrl } from 'utils'
import { Icon } from 'packages/eid-icons'
import ComputerMapDialog from '../MapDialog'
import config from 'config'
import { useIsSmallScreen, useQuery } from 'packages/core'
import { useHistory } from 'react-router'
import { RemainingTime } from 'components'
import { MOBILE_SCREEN_WIDTH } from 'packages/core/utils/constants'
import { useQueryClient } from '@tanstack/react-query'
import { Theme } from '@mui/material/styles'

interface PaddedDivProps {
    isSmallScreen: boolean
}

const PaddedDiv = styled(Box)<PaddedDivProps>(({ isSmallScreen }) => ({
    padding: isSmallScreen ? '0 16px' : '0 31px',
}))

const RequestContainer = styled(Box)(({ theme }) => ({
    borderRadius: '8px',
    boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.05)',
    border: 'solid 1px rgba(0, 0, 0, 0.11)',
    background: theme.palette.background.paper,
    marginTop: '16px',
    paddingBottom: '20px',
    overflow: 'hidden',
}))

const RequestHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: 'solid 1px rgba(0, 0, 0, 0.11)',
    padding: '4px 20px',
    backgroundImage:
        'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
    backgroundSize: '8.00px 8.00px',
})

const HeaderTitle = styled(Box)({
    fontSize: '14px',
    fontWeight: 600,
    marginTop: '5px',
    marginBottom: '5px',
})

const DetailButton = styled(Button)(({ theme }) => ({
    fontSize: '11px',
    color: theme.palette.common.white,
    background: theme.palette.secondary.main,
    borderRadius: '5px',
    padding: '1px 4px 1px',
    '&:hover': {
        background: theme.palette.secondary.main,
    },
}))

const RequestItemShow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 20px',
    marginTop: '10px',
    [`@media (max-width:${MOBILE_SCREEN_WIDTH}px)`]: {
        flexDirection: 'column',
        alignItems: 'start',
        gap: '16px',
    },
})

const RequestItemShowSecond = styled(Box)({
    display: 'flex',
    padding: '10px 20px',
    marginTop: '10px',
    justifyContent: 'space-between',
    [`@media (max-width:${MOBILE_SCREEN_WIDTH}px)`]: {
        flexDirection: 'column',
        alignItems: 'start',
        gap: '16px',
    },
})

const ItemTitle = styled(Box)({
    fontSize: '12px',
    fontWeight: 500,
    color: '#9b9b9b',
    margin: '0px',
    textTransform: 'uppercase',
    [`@media (max-width:${MOBILE_SCREEN_WIDTH}px)`]: {
        fontWeight: '600',
    },
})

const ItemHeading = styled(Box)({
    fontSize: '14px',
    fontWeight: 500,
    margin: '0px',
})

const ItemHeadingLink = styled(ItemHeading)({
    '& a': {
        color: (theme: Theme) => `${theme.palette.primary.main} !important`,
        textDecoration: 'none !important',
    },
})

const ItemTime = styled(Box)(({ theme }) => ({
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.common.black,
    margin: '0px',
    '& button': {
        fontSize: '11px',
        borderRadius: '5px',
        padding: '1px 4px 1px',
        '&:hover': {
            background: theme.palette.secondary.main,
        },
    },
}))

const LocationIcon = styled(Box)(({ theme }) => ({
    height: '20px',
    width: '20px',
    borderRadius: '50%',
    background: theme.palette.common.white,
    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
    textAlign: 'center',
}))

const CheckinButton = styled(Button)(({ theme }) => ({
    fontSize: '11px',
    color: theme.palette.common.white,
    background: theme.palette.primary.main,
    borderRadius: '5px',
    '&:hover': {
        background: theme.palette.primary.main,
    },
}))

const AvatarSection = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    '& > div': {
        height: '20px',
        width: '20px',
    },
})

const AvatarName = styled('span')({
    marginLeft: '5px',
})

export interface ComputerSessionHistoryTileProps {
    item: any
}

export const ComputerSessionHistoryTile: FC<ComputerSessionHistoryTileProps> = (
    props,
) => {
    const { item } = props
    const { t } = useTranslation()
    const query = useQuery()
    const history = useHistory()
    const queryClient = useQueryClient()

    const [showDetailsDialog, setShowDetailsDialog] = useState(false)
    const [showMapDialog, setShowMapDialog] = useState(false)
    const handleDetailClick = () => {
        setShowDetailsDialog(true)
    }
    const isSmallScreen = useIsSmallScreen()

    const {
        mutateAsync: terminateSession,
        data: terminateSessionData,
        isLoading,
    } = useTerminateSession()

    const handleTerminate = () => {
        terminateSession(item.id)
    }

    useEffect(() => {
        if (terminateSessionData && !isLoading) {
            refreshListAFterTerminate()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [terminateSessionData, isLoading])

    const refreshListAFterTerminate = () => {
        try {
            refreshComputerList(queryClient)
            query.delete('selected')
            history.push(`${history.location.pathname}?${query.toString()}`)
        } catch (err) {
            // Ignore
        }
    }

    const ComputersConnectedWhenAndDuration = (name: string, data: any) => {
        const { t } = useTranslation()
        const whenData = data[name]
        let connectedWhen = `0 ${t('min')}`
        if (whenData?.days > 0) {
            connectedWhen = `${whenData.days} ${
                whenData.days > 1 ? t('days') : t('day')
            }`
        }
        if (whenData?.hours > 0) {
            connectedWhen =
                connectedWhen === `0 ${t('min')}`
                    ? `${whenData.hours} ${
                          whenData.hours > 1 ? t('hours') : t('hour')
                      }`
                    : connectedWhen +
                      ` ${whenData.hours} ${
                          whenData.hours > 1 ? t('hours') : t('hour')
                      }`
        }
        if (whenData?.minutes > 0) {
            connectedWhen =
                connectedWhen === `0 ${t('min')}`
                    ? `${whenData.minutes} ${
                          whenData.minutes > 1 ? t('mins') : t('min')
                      }`
                    : connectedWhen +
                      ` ${whenData.minutes} ${
                          whenData.minutes > 1 ? t('mins') : t('min')
                      }`
        }
        return <>{connectedWhen}</>
    }

    const bindComputerName = () => {
        return `${item.computerName}, ${t('Common_StartTime')} ${moment
            .utc(item.startTimeUtc)
            .local()
            .format('L LT')}`
    }

    return (
        <>
            <PaddedDiv isSmallScreen={isSmallScreen}>
                <RequestContainer>
                    <RequestHeader>
                        <HeaderTitle>
                            {item.computerFriendlyName
                                ? bindComputerName()
                                : '---'}
                        </HeaderTitle>
                        <Box>
                            <DetailButton onClick={() => handleDetailClick()}>
                                {t('ManageAccess_Details')}
                            </DetailButton>
                        </Box>
                    </RequestHeader>
                    <RequestItemShow>
                        <Box>
                            <ItemTitle>{t('Common_WhoConnected')}</ItemTitle>
                            <ItemHeading>
                                <AvatarSection>
                                    <Avatar
                                        size="small"
                                        src={getFullImageUrl(
                                            item.checkedOutByPersonThumbUrl,
                                        )}
                                    />
                                    <AvatarName>
                                        {item.checkedOutByPersonName
                                            ? item.checkedOutByPersonName
                                            : '---'}
                                    </AvatarName>
                                </AvatarSection>
                            </ItemHeading>
                        </Box>
                        <Box>
                            <ItemTitle>{t('Common_Computer')}</ItemTitle>
                            <ItemHeadingLink>
                                {item?.computerId &&
                                item.computerFriendlyName ? (
                                    <a
                                        rel="noreferrer"
                                        target="_blank"
                                        href={`${config.APP_SUBPATH}/Computers?selected=${item.computerId}`}
                                    >
                                        {item.computerFriendlyName
                                            ? item.computerFriendlyName
                                            : '---'}
                                    </a>
                                ) : item.computerFriendlyName ? (
                                    item.computerFriendlyName
                                ) : (
                                    '---'
                                )}
                            </ItemHeadingLink>
                        </Box>
                        <Box>
                            <ItemTitle>{t('Common_Credential')}</ItemTitle>
                            <ItemHeading>
                                {item.externalCredentialFriendlyName
                                    ? item.externalCredentialFriendlyName
                                    : '---'}
                            </ItemHeading>
                        </Box>
                        {item.canTerminateSession ? (
                            <Box>
                                <ItemTitle>{t('RemainingTime')}</ItemTitle>
                                <ItemTime>
                                    <RemainingTime
                                        endTime={item.credCheckedOutEndTimeUtc}
                                    />
                                </ItemTime>
                            </Box>
                        ) : (
                            <Box>
                                <ItemTitle>
                                    {t('Common_ConnectedWhen')}
                                </ItemTitle>
                                <ItemTime>
                                    {ComputersConnectedWhenAndDuration(
                                        'when',
                                        item,
                                    )}
                                </ItemTime>
                            </Box>
                        )}
                    </RequestItemShow>

                    <RequestItemShowSecond>
                        <Box>
                            <ItemTitle>{t('Common_Duration')}</ItemTitle>
                            <ItemTime>
                                {ComputersConnectedWhenAndDuration(
                                    'duration',
                                    item,
                                )}
                            </ItemTime>
                        </Box>
                        <Box>
                            <ItemTitle>{t('Common_LOA')}</ItemTitle>
                            <ItemTime>
                                {item.assuranceLevel
                                    ? item.assuranceLevel
                                    : '---'}
                            </ItemTime>
                        </Box>
                        <Box>
                            <ItemTitle>{t('Common_Location')}</ItemTitle>
                            <Tooltip
                                title={`${
                                    item.latitude &&
                                    item.longitude &&
                                    config.GOOGLE_MAP_API_KEY
                                        ? t('Common_ViewLocationOnMap')
                                        : !config.GOOGLE_MAP_API_KEY
                                        ? t('Common_LocationServiceNotEnabled')
                                        : t('Common_LocationDataNotAvailable')
                                }`}
                            >
                                <LocationIcon>
                                    <Icon
                                        onClick={() => setShowMapDialog(true)}
                                        name="Map"
                                    />
                                </LocationIcon>
                            </Tooltip>
                        </Box>

                        {item.canTerminateSession ? (
                            <Box>
                                <ItemTime>
                                    <CheckinButton
                                        onClick={() => handleTerminate()}
                                    >
                                        {t('Common_Disconnect')}
                                    </CheckinButton>
                                </ItemTime>
                            </Box>
                        ) : (
                            <></>
                        )}
                    </RequestItemShowSecond>
                </RequestContainer>
            </PaddedDiv>
            {showDetailsDialog && (
                <ComputerSessionHistoryDetails
                    handleTerminate={handleTerminate}
                    item={item}
                    open={showDetailsDialog}
                    onClose={() => setShowDetailsDialog(false)}
                    title={
                        item.computerFriendlyName ? bindComputerName() : '---'
                    }
                />
            )}
            {item.latitude && item.longitude && showMapDialog ? (
                <ComputerMapDialog
                    latitude={item.latitude}
                    langitutde={item.longitude}
                    open={showMapDialog}
                    onClose={() => setShowMapDialog(false)}
                    title={item.computerFriendlyName}
                />
            ) : (
                <></>
            )}
        </>
    )
}

export default ComputerSessionHistoryTile
